import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import MenuPaintedButton from '../components/MenuPaintedButton';
import {
  COUNTER_STRIKE_KEEP_BUTTON_PATH,
  COUNTER_STRIKE_STRIKE_BUTTON_PATH,
} from '../config/welcomeAssets';
import './CounterStrikeDialog.css';

/** Last N seconds of bot CS wait shown as a countdown on the card (must match server). */
const COUNTER_STRIKE_UI_COUNTDOWN_SECONDS = 5;

/**
 * Counter Strike Dialog Component
 * Displays when a Counter Strike order is drawn from a dismantled barrier
 * Shows order in extended view with counter strike title and action buttons
 * Provides "Strike!" and "Keep" buttons
 * When "Strike!" is clicked, allows direct zone targeting like carousel
 * 
 * Mobile support: When isMobile is true, displays with smaller card size
 * to fit the 932x430 mobile viewport.
 *
 * When the bot is waiting on this CS (`gameState.botCounterStrikeWaitDeadlineMs`),
 * the last 5 seconds show a countdown overlay on the card before auto-keep.
 */
const CounterStrikeDialog = ({ orders, onSelectZone, onKeepOrder, playerId, onStrikeModeActivated, isMobile = false, processingCounterStrikeId = null, handSize = 0, gameState = null }) => {
  const { t } = useTranslation();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [strikeMode, setStrikeMode] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(null);

  // Initialize selected order from incoming orders
  // ✅ FIX: Only show orders that aren't currently being processed
  // ✅ FIX: When orders array changes (e.g., after playing one counterstrike), always update selectedOrder
  // to the first order in the array that isn't being processed, and reset strikeMode to show the dialog again
  useEffect(() => {
    if (orders && orders.length > 0) {
      // ✅ FIX: Filter out orders that are currently being processed
      const availableOrders = processingCounterStrikeId 
        ? orders.filter(o => o.instanceId !== processingCounterStrikeId)
        : orders;
      
      if (availableOrders.length > 0) {
        // Always set to first available order when orders change
        const firstOrder = availableOrders[0];
        // Only update if the selected order is different or doesn't exist
        setSelectedOrder(prev => {
          // If previous order is not in the available orders array, switch to first order
          if (!prev || !availableOrders.find(o => o.instanceId === prev.instanceId)) {
            return firstOrder;
          }
          // Otherwise keep the same order if it's still in the array
          return prev;
        });
        // Always reset strikeMode when orders change to allow dialog to show for subsequent counterstrikes
        setStrikeMode(false);
      } else {
        // All orders are being processed - hide dialog but keep selectedOrder
        setStrikeMode(false);
      }
    } else {
      setSelectedOrder(null);
      setStrikeMode(false);
    }
  }, [orders, processingCounterStrikeId]);

  // Bot wait deadline: show 5..1 countdown on the card in the final seconds
  useEffect(() => {
    const deadlineMs = gameState?.botCounterStrikeWaitDeadlineMs;
    if (deadlineMs == null || !Number.isFinite(Number(deadlineMs))) {
      setCountdownSeconds(null);
      return undefined;
    }

    const tick = () => {
      const remainingMs = Number(deadlineMs) - Date.now();
      if (remainingMs <= 0) {
        setCountdownSeconds(0);
        return;
      }
      const remainingSec = Math.ceil(remainingMs / 1000);
      if (remainingSec <= COUNTER_STRIKE_UI_COUNTDOWN_SECONDS) {
        setCountdownSeconds(remainingSec);
      } else {
        setCountdownSeconds(null);
      }
    };

    tick();
    const intervalId = setInterval(tick, 200);
    return () => clearInterval(intervalId);
  }, [gameState?.botCounterStrikeWaitDeadlineMs]);

  const handleStrike = () => {
    if (selectedOrder) {
      setStrikeMode(true);
      // Use the same mechanism as carousel - set selectedOrderInstanceId
      if (onStrikeModeActivated) {
        onStrikeModeActivated(selectedOrder);
      }
      // ✅ FIX: Clear selectedOrder immediately to prevent dialog from showing it while processing
      // The order will be filtered out by processingCounterStrikeId check anyway, but this ensures clean state
      setSelectedOrder(null);
    }
  };

  const handleKeep = () => {
    if (selectedOrder) {
      onKeepOrder(selectedOrder.id, selectedOrder.instanceId);
      // ✅ FIX: Clear selectedOrder immediately to prevent dialog from showing it while processing
      setSelectedOrder(null);
    }
  };

  // Early returns must come AFTER hooks to satisfy React rules of hooks
  if (!orders || orders.length === 0) {
    return null;
  }
  
  // ✅ FIX: Don't show dialog if all orders are being processed
  const availableOrders = processingCounterStrikeId 
    ? orders.filter(o => o.instanceId !== processingCounterStrikeId)
    : orders;
  
  if (availableOrders.length === 0) {
    return null;
  }

  // Hide dialog when in strike mode to allow zone clicking
  if (strikeMode) {
    return null;
  }

  return (
    <div className="counter-strike-overlay" data-no-battle-flashlight="true">
      <div 
        className="counter-strike-dialog"
        style={isMobile ? {
          transform: 'scale(0.45)',
          transformOrigin: 'center center'
        } : {}}
      >
        <div className="counter-strike-header">
          <h2 style={isMobile ? { fontSize: '32px' } : {}}>{t('battleUI.counterStrikeTitle')}</h2>
        </div>

        <div className="counter-strike-order-display">
          {selectedOrder && (
            <div className="counter-strike-order-card">
              <OrderDesign
                order={selectedOrder}
                count={0}
                minimal={false}
                isOOR={false}
                isSelected={false}
                isTarget={false}
                isCounterStrike={true}
                gameState={gameState}
                userId={playerId}
              />
              {countdownSeconds != null && countdownSeconds > 0 && (
                <div
                  className="counter-strike-auto-keep-countdown"
                  aria-live="polite"
                  aria-label={t('battleUI.counterStrikeAutoKeepCountdown', { seconds: countdownSeconds })}
                >
                  <span className="counter-strike-auto-keep-countdown__number">
                    {countdownSeconds}
                  </span>
                  <span className="counter-strike-auto-keep-countdown__label">
                    {t('battleUI.counterStrikeAutoKeepHint')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`counter-strike-action-buttons${isMobile ? ' counter-strike-action-buttons--mobile' : ''}`}>
          <MenuPaintedButton
            type="button"
            className="counter-strike-strike-button"
            imagePath={COUNTER_STRIKE_STRIKE_BUTTON_PATH}
            onClick={handleStrike}
          >
            {t('battleUI.counterStrikeStrike')}
          </MenuPaintedButton>
          {handSize < 10 && (
            <MenuPaintedButton
              type="button"
              className="counter-strike-keep-button"
              imagePath={COUNTER_STRIKE_KEEP_BUTTON_PATH}
              onClick={handleKeep}
              title={t('battleUI.counterStrikeAddToHand')}
            >
              {t('battleUI.keep')}
            </MenuPaintedButton>
          )}
          {handSize >= 10 && (
            <div style={{ 
              fontSize: isMobile ? '16px' : '14px', 
              color: '#ff6b6b', 
              marginTop: '8px',
              textAlign: 'center'
            }}>
              {t('battleUI.counterStrikeHandFull')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CounterStrikeDialog;
